import { useEffect, useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AppIcon } from '@/components/ui/icons';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/features/ui/components/Button';
import { Modal, ModalBody, ModalFooter } from '@/features/ui/components/Modal';
import { useAuth } from '@/contexts/AuthContext';
import { userService } from '@/services/users/UserService';

interface TwoFactorSetupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

type SetupStep = 'intro' | 'qrcode' | 'verify' | 'complete';

const STEP_DESCRIPTION: Record<SetupStep, string> = {
  intro: 'Connect a TOTP authenticator to this AX1 account.',
  qrcode: 'Scan the provider-issued QR code.',
  verify: 'Confirm the current six-digit code.',
  complete: 'Supabase confirmed this session at AAL2.',
};

const TwoFactorSetupModal = ({ isOpen, onClose, onSuccess }: TwoFactorSetupModalProps) => {
  const { user } = useAuth();
  const [step, setStep] = useState<SetupStep>('intro');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);
  const [secretCopied, setSecretCopied] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setStep('intro');
    setError('');
    setFactorId(null);
    setQrCode(null);
    setSecret(null);
    setShowSecret(false);
    setSecretCopied(false);
    setVerifyCode('');
  }, [isOpen]);

  const handleClose = () => {
    if (user?.id && factorId && step !== 'complete') {
      void userService.cancelMfaEnrollment(user.id, factorId);
    }
    onClose();
  };

  const handleStartEnrollment = async () => {
    if (!user?.id) {
      setError('Your authenticated account could not be resolved. Sign in again.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const result = await userService.enrollMfaTotp(user.id);
      if (!result.success || !result.factorId || !result.qrCode || !result.secret) {
        setError(result.error?.message || 'Could not start authenticator enrollment.');
        return;
      }
      setFactorId(result.factorId);
      setQrCode(result.qrCode);
      setSecret(result.secret);
      setStep('qrcode');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not start authenticator enrollment.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyEnrollment = async () => {
    if (!user?.id || !factorId || verifyCode.length !== 6) return;
    setIsLoading(true);
    setError('');
    try {
      const result = await userService.verifyMfaEnrollment(user.id, factorId, verifyCode);
      if (!result.success) {
        setError(result.error?.message || 'The verification code was not accepted.');
        return;
      }
      setStep('complete');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Authenticator verification failed.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopySecret = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setSecretCopied(true);
    } catch {
      setError('Could not copy the setup key. Select it manually instead.');
    }
  };

  const handleComplete = () => {
    onSuccess?.();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Set up two-factor authentication"
      description={STEP_DESCRIPTION[step]}
      size="lg"
      closeOnOverlayClick={false}
    >
      <ModalBody className="space-y-5">
        {error && (
          <Alert variant="destructive" aria-live="polite">
            <AppIcon name="alertCircle" className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {step === 'intro' && (
          <div className="space-y-5">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-accent-primary/10">
                <AppIcon name="smartphone" className="h-5 w-5 text-accent-primary" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-text-primary">Connect an authenticator app</h3>
                <p className="mt-1 text-sm leading-6 text-text-muted">
                  AX1 uses provider-backed time-based codes. Workspace access remains blocked until the new factor is verified.
                </p>
              </div>
            </div>
            <Alert variant="info">
              <AppIcon name="shield" className="h-4 w-4" />
              <AlertDescription>
                Have Google Authenticator, Microsoft Authenticator, Authy, 1Password, or another TOTP app ready.
              </AlertDescription>
            </Alert>
          </div>
        )}

        {step === 'qrcode' && (
          <div className="space-y-5">
            <div className="text-center">
              <p className="mb-4 text-sm text-text-muted">Scan this QR code with your authenticator app.</p>
              <div className="inline-flex rounded-[var(--radius-sm)] border border-border-subtle bg-white p-4">
                {qrCode
                  ? <img src={qrCode} alt="Authenticator enrollment QR code" className="h-48 w-48" />
                  : <AppIcon name="loader2" className="h-8 w-8 animate-spin text-text-muted" />}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="mfa-setup-secret">Manual setup key</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="mfa-setup-secret"
                  readOnly
                  value={showSecret ? secret ?? '' : '•••• •••• •••• ••••'}
                  className="font-mono"
                />
                <Button type="button" variant="secondary" onClick={() => setShowSecret((visible) => !visible)}>
                  {showSecret ? 'Hide' : 'Show'}
                </Button>
                <Button type="button" variant="secondary" onClick={handleCopySecret} disabled={!secret}>
                  {secretCopied ? 'Copied' : 'Copy'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 'verify' && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mfa-enrollment-code">Verification code</Label>
              <Input
                id="mfa-enrollment-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                enterKeyHint="done"
                maxLength={6}
                value={verifyCode}
                onChange={(event) => setVerifyCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={isLoading}
                className="h-12 text-center font-mono text-xl tracking-[0.35em]"
                placeholder="000000"
              />
              <p className="text-xs text-text-muted">Enter the code currently shown by your authenticator app.</p>
            </div>
          </div>
        )}

        {step === 'complete' && (
          <div className="py-4 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-status-success-soft">
              <AppIcon name="checkCircle2" className="h-6 w-6 text-status-success-foreground" />
            </div>
            <h3 className="mt-4 text-base font-semibold text-text-primary">Authenticator verified</h3>
            <p className="mt-1 text-sm text-text-muted">
              Supabase confirmed this session at AAL2. AX1 will now re-check your account authority.
            </p>
          </div>
        )}
      </ModalBody>

      <ModalFooter>
        {step === 'intro' && (
          <Button type="button" loading={isLoading} onClick={handleStartEnrollment}>Start setup</Button>
        )}
        {step === 'qrcode' && (
          <>
            <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
            <Button type="button" onClick={() => setStep('verify')}>Continue</Button>
          </>
        )}
        {step === 'verify' && (
          <>
            <Button type="button" variant="secondary" onClick={() => setStep('qrcode')} disabled={isLoading}>Back</Button>
            <Button
              type="button"
              loading={isLoading}
              disabled={verifyCode.length !== 6}
              onClick={handleVerifyEnrollment}
            >
              Verify authenticator
            </Button>
          </>
        )}
        {step === 'complete' && (
          <Button type="button" onClick={handleComplete}>Continue to AX1</Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default TwoFactorSetupModal;
